/* eslint-disable @typescript-eslint/no-explicit-any */
import { generateMetricsFilterParams } from "../components/admin/analytics/utils/utils";
import { PieChartData, ScorecardData, LineChartData } from "../store/reducers/admin/analytics";
import { AnalyticsEvent } from "../types/working-model";
import { GetMetrics, GetJson } from "./helpers";

const withAdditionalParams = (filterParam: string, additionalParams: Record<string, string>) => {
	if (Object.keys(additionalParams).length) {
		const _additionalParams = new URLSearchParams();
		Object.entries(additionalParams).forEach(([key, val]) => {
			_additionalParams.set(key, val);
		});
		if (_additionalParams.toString()) {
			const prefix = filterParam ? '&' : '?';
			filterParam += prefix + _additionalParams.toString();
		}
	}
	return filterParam;
};

const filterParams = (
	start: number | undefined,
	end: number | undefined,
	audienceList: string[] | undefined,
	language?: string,
	additionalParams = {},
) => {
	const _start = start ? new Date(start) : null;
	const _end = end ? new Date(end) : null;
	const filterParam = generateMetricsFilterParams({ start: _start, end: _end, audienceList, language });
	// stringify additionalParams values
	const stringifiedAdditionalParams = Object.entries(additionalParams).reduce((accum, curr) => {
		return {
			...accum,
			[curr[0]]: typeof curr[1] === 'string' ? curr[1] : JSON.stringify(curr[1])
		};
	}, {});
	return withAdditionalParams(filterParam, stringifiedAdditionalParams);
};

const Get = (path: string, token: string, abortable: AbortController | undefined) => {
	return GetMetrics(path, token, undefined, undefined, false, abortable);
};

export class AbortError {
	constructor(public message: string) {
	}
}

class AnalyticsFetcher {
	map: Map<string, AbortController> = new Map();

	makeAbortable = (name: string) => {
		const abortController = new AbortController();
		this.map.set(name, abortController);
		return abortController;
	};

	abortLast = (name: string) => {
		try {
			this.map.get(name)?.abort(new AbortError('User made another request using ' + name));
			this.map.delete(name);
		} catch {
			// aborted request
		}
	};

	abortAll = () => {
		for (const [name, abortController] of this.map.entries()) {
			try {
				abortController.abort(new AbortError('User navigated away, cancelling ' + name));
				this.map.delete(name);
			} catch {
				// aborted request
			}
		}
	};

	abortSession = () => {
		const sessionReqs = ['GetSessionAllChartData', 'GetSessionUserSurvey', 'GetSessionViews', 'GetSessionDuration', 'GetSessionRetentionAverage', 'GetSessionUniqueAttendance', 'GetSession90SecondVideoViewers', 'GetSessionReactions', 'GetSessionEngagement'];
		for (const req of sessionReqs) {
			try {
				this.map.get(req)?.abort(new AbortError('User navigated away, cancelling ' + req));
				this.map.delete(req);
			} catch {
				// aborted request
			}
		}
	};

	abortEvent = () => {
		const eventReqs = ['GetEventAllChartData', 'GetEventViews', 'GetEventDuration', 'GetEventRetentionAverage', 'GetEventUniqueAttendance', 'GetEventRegistrations', 'GetEventEmails', 'GetEventWatchtime', 'GetLiveAndOnDemandEventViews'];
		for (const req of eventReqs) {
			try {
				this.map.get(req)?.abort(new AbortError('User navigated away, cancelling ' + req));
				this.map.delete(req);
			} catch {
				// aborted request
			}
		}
	};

	abortChannel = () => {
		const channelReqs = [
			'GetChannelAllChartData',
			'GetChannelRegistrations',
			'GetUniqueChannelRegistrations',
			'GetChannelDurations',
			'GetChannelViews',
			'GetChannelViewsPerEvent',
			'GetChannelWatchtime',
			'GetChannelRetentionAverage',
			'GetChannelRetentionBuckets',
			'GetChannelUniqueAttendance',
			'GetChannelAverageUserWatchtime'
		];
		for (const req of channelReqs) {
			this.map.get(req)?.abort(new AbortError('User navigated away, cancelling ' + req));
			this.map.delete(req);
		}
	};

	GetChannelEvents = (
		channel: number,
		token: string,
		start?: number,
		end?: number,
		audienceList?: string[],
		offset?: number,
		search?: string,
		sortBy?: string
	): Promise<AnalyticsEvent[]> => {
		const optionalParams: Record<string, string | number> = {};

		if (search) {
			optionalParams.search = search;
		}

		if (sortBy) {
			optionalParams.sortBy = sortBy;
		}

		if (offset) {
			optionalParams.offset = offset;
			optionalParams.limit = 10;
		}

		return GetMetrics(`/v3/admin/metrics/channels/${channel}/events${filterParams(start, end, audienceList, undefined, optionalParams)}`, token);
	};

	GetChannelEvent = (
		channel: number,
		eventUuid: string,
		token: string,
		start?: number,
		end?: number,
		audienceList?: string[],
	): Promise<AnalyticsEvent[]> => {
		this.abortLast('GetChannelEvent');
		const abortable = this.makeAbortable('GetChannelEvent');
		return Get(`/v3/admin/metrics/channels/${channel}/event/${eventUuid}${filterParams(start, end, audienceList, undefined)}`, token, abortable);
	};

	GetChannelSessionsTotal = (channel: number, token: string) => {
		this.abortLast('GetChannelSessionsTotal');
		const abortController = this.makeAbortable('GetChannelSessionsTotal');
		return GetJson<{ total: number }>({ path: `/v3/admin/metrics/channels/${channel}/sessions/total`, token, metrics: true, abortController }).then((res) => res.total);
	};

	GetChannelAllChartData = (channel: number, token: string, start?: number, end?: number, audienceList?: string[]): Promise<Array<PieChartData>> => {
		this.abortLast('GetChannelAllChartData');
		const abortController = this.makeAbortable('GetChannelAllChartData');
		return Get(`/v3/admin/metrics/channels/${channel}/all-data${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetChannelData = (channel: number, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetChannelData');
		const abortController = this.makeAbortable('GetChannelData');
		return Get(`/v3/admin/metrics/channels/${channel}${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetChannelAudience = (channel: number, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetChannelAudience');
		const abortController = this.makeAbortable('GetChannelAudience');
		return Get(`/v3/admin/metrics/channels/${channel}/audience${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetChannelRegistrations = (channel: number, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetChannelRegistrations');
		const abortController = this.makeAbortable('GetChannelRegistrations');
		return Get(`/v3/admin/metrics/channels/${channel}/registrations${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetUniqueChannelRegistrations = (channel: number, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetUniqueChannelRegistrations');
		const abortController = this.makeAbortable('GetUniqueChannelRegistrations');
		return Get(`/v3/admin/metrics/channels/${channel}/unique-registrations${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetChannelDurations = (channel: number, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetChannelDurations');
		const abortController = this.makeAbortable('GetChannelDurations');
		return Get(`/v3/admin/metrics/channels/${channel}/averageDuration${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetChannelViews = (channel: number, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetChannelViews');
		const abortController = this.makeAbortable('GetChannelViews');
		return Get(`/v3/admin/metrics/channels/${channel}/views${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetChannelViewsPerEvent = (channel: number, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetChannelViewsPerEvent');
		const abortController = this.makeAbortable('GetChannelViewsPerEvent');
		return Get(`/v3/admin/metrics/channels/${channel}/views-per-event${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetChannelWatchtime = (channel: number, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetChannelWatchtime');
		const abortController = this.makeAbortable('GetChannelWatchtime');
		return Get(`/v3/admin/metrics/channels/${channel}/watchtime${filterParams(start, end, audienceList, undefined)}`, token, abortController);
	};

	GetChannelRetentionAverage = (channel: number, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetChannelRetentionAverage');
		const abortController = this.makeAbortable('GetChannelRetentionAverage');
		return Get(`/v3/admin/metrics/channels/${channel}/retention/average${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetChannelRetentionBuckets = (channel: number, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetChannelRetentionBuckets');
		const abortController = this.makeAbortable('GetChannelRetentionBuckets');
		return Get(`/v3/admin/metrics/channels/${channel}/retention/buckets${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetChannelUniqueAttendance = (channel: number, token: string, start?: number, end?: number, audienceList?: string[]): Promise<Record<string, number>> => {
		this.abortLast('GetChannelUniqueAttendance');
		const abortController = this.makeAbortable('GetChannelUniqueAttendance');
		return Get(`/v3/admin/metrics/channels/${channel}/unique-attendance${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetChannelAverageUserWatchtime = (channelID: number, token: string, start?: number, end?: number, audienceList?: string[]): Promise<ScorecardData> => {
		this.abortLast('GetChannelAverageUserWatchtime');
		const abortController = this.makeAbortable('GetChannelAverageUserWatchtime');
		return Get(`/v3/admin/metrics/channels/${channelID}/averageUserWatchtime${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetEventAllChartData = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetEventAllChartData');
		const abortController = this.makeAbortable('GetEventAllChartData');
		return Get(`/v3/admin/metrics/events/${uuid}/all-data${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetEventViews = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetEventViews');
		const abortController = this.makeAbortable('GetEventViews');
		return Get(`/v3/admin/metrics/events/${uuid}/views${filterParams(start, end, audienceList)}`, token, abortController);
	};
	GetLiveAndOnDemandEventViews = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<ScorecardData> => {
		this.abortLast('GetLiveAndOnDemandEventViews');
		const abortController = this.makeAbortable('GetLiveAndOnDemandEventViews');
		return Get(`/v3/admin/metrics/events/${uuid}/total-page-views${filterParams(start, end, audienceList)}`, token, abortController);
	};
	GetEventDuration = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetEventDuration');
		const abortController = this.makeAbortable('GetEventDuration');
		return Get(`/v3/admin/metrics/events/${uuid}/averageDuration${filterParams(start, end, audienceList)}`, token, abortController);
	};
	GetEventRetentionAverage = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetEventRetentionAverage');
		const abortController = this.makeAbortable('GetEventRetentionAverage');
		return Get(`/v3/admin/metrics/events/${uuid}/retention/average${filterParams(start, end, audienceList)}`, token, abortController);
	};
	GetEventRetentionBuckets = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetEventRetentionBuckets');
		const abortController = this.makeAbortable('GetEventRetentionBuckets');
		return Get(`/v3/admin/metrics/events/${uuid}/retention/buckets${filterParams(start, end, audienceList)}`, token, abortController);
	};
	GetEventRegistrations = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetEventRegistrations');
		const abortController = this.makeAbortable('GetEventRegistrations');
		return Get(`/v3/admin/metrics/events/${uuid}/registrations${filterParams(start, end, audienceList)}`, token, abortController);
	};
	GetEventEmails = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetEventEmails');
		const abortController = this.makeAbortable('GetEventEmails');
		return Get(`/v3/admin/metrics/events/${uuid}/emails${filterParams(start, end, audienceList)}`, token, abortController);
	};
	GetEventWatchtime = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetEventWatchtime');
		const abortController = this.makeAbortable('GetEventWatchtime');
		return Get(`/v3/admin/metrics/events/${uuid}/watchtime${filterParams(start, end, audienceList)}`, token, abortController);
	};
	GetEventSessions = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetEventSessions');
		const abortController = this.makeAbortable('GetEventSessions');
		return Get(`/v3/admin/metrics/events/${uuid}/sessions${filterParams(start, end, audienceList)}`, token, abortController);
	};
	GetEventSessionsPageViews = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetEventSessionsPageViews');
		const abortController = this.makeAbortable('GetEventSessionsPageViews');
		return Get(`/v3/admin/metrics/events/${uuid}/page-views-per-session${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetEventAverageSessionAttendance = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<ScorecardData> => {
		this.abortLast('GetEventAverageSessionAttendance');
		const abortController = this.makeAbortable('GetEventAverageSessionAttendance');
		return Get(`/v3/admin/metrics/events/${uuid}/sessionAttendance${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetEventUniqueAttendance = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetEventUniqueAttendance');
		const abortController = this.makeAbortable('GetEventUniqueAttendance');
		return Get(`/v3/admin/metrics/events/${uuid}/unique-attendance${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetEventAttendanceRate = (uuid: string, token: string): Promise<any> => {
		this.abortLast('GetEventAttendanceRate');
		const abortController = this.makeAbortable('GetEventAttendanceRate');
		return Get(`/v3/admin/metrics/events/${uuid}/attendance-rate`, token, abortController);
	};

	GetEventSessionsListUniqueAttendees = (eventUUID: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<ScorecardData> => {
		this.abortLast('GetEventSessionsListUniqueAttendees');
		const abortController = this.makeAbortable('GetEventSessionsListUniqueAttendees');
		return Get(`/v3/admin/metrics/events/${eventUUID}/sessions/unique-attendance${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetEventSessionsListAvgRetention = (eventUUID: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<ScorecardData> => {
		this.abortLast('GetEventSessionsListAvgRetention');
		const abortController = this.makeAbortable('GetEventSessionsListAvgRetention');
		return Get(`/v3/admin/metrics/events/${eventUUID}/sessions/avg-retention${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetEventAverageUserWatchtime = (eventUUID: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<ScorecardData> => {
		this.abortLast('GetEventAverageUserWatchtime');
		const abortController = this.makeAbortable('GetEventAverageUserWatchtime');
		return Get(`/v3/admin/metrics/events/${eventUUID}/averageUserWatchtime${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetFirstSessionStartTime = (eventUUID: string, token: string): Promise<any> => {
		this.abortLast('GetFirstSessionStartTime');
		const abortController = this.makeAbortable('GetFirstSessionStartTime');
		return Get(`/v3/admin/metrics/events/${eventUUID}/first-session-start-time`, token, abortController);
	};

	GetPaginatedSessionAttendees = (
		sessionUuid: string,
		token: string,
		channel: number,
		start?: number,
		end?: number,
		audienceList?: string[],
		language?: string,
		pageNumber = 1,
		textSearch?: string,
		sortBy = 'attendee',
		sortOrder = 0
	): Promise<ScorecardData> => {
		this.abortLast('GetPaginatedSessionAttendees');
		const abortController = this.makeAbortable('GetPaginatedSessionAttendees');
		return Get(`/v3/admin/metrics/sessions/${sessionUuid}/attendees${filterParams(
			start,
			end,
			audienceList,
			language,
			{
				page: pageNumber,
				sortBy,
				sortOrder,
				...(textSearch ? { textSearch } : {}),
				channel
			}
		)}`, token, abortController);
	};

	GetSessionAllChartData = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[], language?: string): Promise<any> => {
		this.abortLast('GetSessionAllChartData');
		const abortController = this.makeAbortable('GetSessionAllChartData');
		return Get(`/v3/admin/metrics/sessions/${uuid}/all-data${filterParams(start, end, audienceList, language)}`, token, abortController);
	};

	GetSessionUserSurvey = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[], language?: string): Promise<PieChartData[]> => {
		this.abortLast('GetSessionUserSurvey');
		const abortController = this.makeAbortable('GetSessionUserSurvey');
		return Get(`/v3/admin/metrics/sessions/${uuid}/userSurveys${filterParams(start, end, audienceList, language)}`, token, abortController);
	};
	GetSessionViews = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[], language?: string): Promise<any> => {
		this.abortLast('GetSessionViews');
		const abortController = this.makeAbortable('GetSessionViews');
		return Get(`/v3/admin/metrics/sessions/${uuid}/views${filterParams(start, end, audienceList, language)}`, token, abortController);
	};
	GetSessionDuration = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetSessionDuration');
		const abortController = this.makeAbortable('GetSessionDuration');
		return Get(`/v3/admin/metrics/sessions/${uuid}/averageDuration${filterParams(start, end, audienceList)}`, token, abortController);
	};
	GetSessionRetentionAverage = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetSessionRetentionAverage');
		const abortController = this.makeAbortable('GetSessionRetentionAverage');
		return Get(`/v3/admin/metrics/sessions/${uuid}/retention/average${filterParams(start, end, audienceList)}`, token, abortController);
	};
	GetSessionRegistrations = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetSessionRegistrations');
		const abortController = this.makeAbortable('GetSessionRegistrations');
		return Get(`/v3/admin/metrics/sessions/${uuid}/registrations${filterParams(start, end, audienceList)}`, token, abortController);
	};
	GetSessionEmails = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetSessionEmails');
		const abortController = this.makeAbortable('GetSessionEmails');
		return Get(`/v3/admin/metrics/sessions/${uuid}/emails${filterParams(start, end, audienceList)}`, token, abortController);
	};
	GetSessionWatchtime = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[], language?: string): Promise<any> => {
		this.abortLast('GetSessionWatchtime');
		const abortController = this.makeAbortable('GetSessionWatchtime');
		return Get(`/v3/admin/metrics/sessions/${uuid}/watchtime${filterParams(start, end, audienceList, language)}`, token, abortController);
	};
	GetAverageSessionWatchtimes = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[], language?: string): Promise<any> => {
		this.abortLast('GetAverageSessionWatchtimes');
		const abortController = this.makeAbortable('GetAverageSessionWatchtimes');
		return Get(`/v3/admin/metrics/sessions/${uuid}/average-watchtimes${filterParams(start, end, audienceList, language)}`, token, abortController);
	};
	GetSessionTimeline = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetSessionTimeline');
		const abortController = this.makeAbortable('GetSessionTimeline');
		return Get(`/v3/admin/metrics/sessions/${uuid}/timeline${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetHighestConcurrent = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetHighestConcurrent');
		const abortController = this.makeAbortable('GetHighestConcurrent');
		return Get(`/v3/admin/metrics/sessions/${uuid}/max-concurrent${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetSessionPageViews = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[], language?: string): Promise<Record<string, number>> => {
		this.abortLast('GetSessionPageViews');
		const abortController = this.makeAbortable('GetSessionPageViews');
		return Get(`/v3/admin/metrics/sessions/${uuid}/page-views${filterParams(start, end, audienceList, language)}`, token, abortController);
	};

	GetSessionUniqueAttendance = (session: string, token: string, start?: number, end?: number, audienceList?: string[], language?: string): Promise<Record<string, number>> => {
		this.abortLast('GetSessionUniqueAttendance');
		const abortController = this.makeAbortable('GetSessionUniqueAttendance');
		return Get(`/v3/admin/metrics/sessions/${session}/unique-attendance${filterParams(start, end, audienceList, language)}`, token, abortController);
	};

	GetHighestConcurrentLang = (uuid: string, lang: string, token: string, start?: number, end?: number, audienceList?: string[]): Promise<any> => {
		this.abortLast('GetHighestConcurrentLang');
		const abortController = this.makeAbortable('GetHighestConcurrentLang');
		return Get(`/v3/admin/metrics/sessions/${uuid}/max-concurrent/${lang}${filterParams(start, end, audienceList)}`, token, abortController);
	};

	GetSession90SecondVideoViewers = (uuid: string, token: string, start: number | undefined, end: number | undefined, audienceLists: string[] | undefined, language: string | undefined): Promise<ScorecardData> => {
		this.abortLast('GetSession90SecondVideoViewers');
		const abortController = this.makeAbortable('GetSession90SecondVideoViewers');
		return Get(`/v3/admin/metrics/sessions/${uuid}/90-sec-viewers${filterParams(start, end, audienceLists, language)}`, token, abortController);
	};

	GetSessionRetentionTimeline = (uuid: string, token: string, start?: number, end?: number, audienceList?: string[], language?: string): Promise<LineChartData> => {
		this.abortLast('GetSessionRetentionTimeline');
		const abortController = this.makeAbortable('GetSessionRetentionTimeline');
		return Get(`/v3/admin/metrics/sessions/${uuid}/retention${filterParams(start, end, audienceList, language)}`, token, abortController);
	};

	GetSessionReactions = (sessionUUID: string, token: string, start?: number, end?: number, audienceList?: string[], language?: string): Promise<PieChartData[]> => {
		this.abortLast('GetSessionReactions');
		const abortController = this.makeAbortable('GetSessionReactions');
		return Get(`/v3/admin/metrics/sessions/${sessionUUID}/reactions${filterParams(start, end, audienceList, language)}`, token, abortController);
	};

	GetSessionEngagement = (sessionUUID: string, token: string, start?: number, end?: number, audienceList?: string[], language?: string): Promise<LineChartData> => {
		this.abortLast('GetSessionEngagement');
		const abortController = this.makeAbortable('GetSessionEngagement');
		return Get(`/v3/admin/metrics/sessions/${sessionUUID}/engagement${filterParams(start, end, audienceList, language)}`, token, abortController);
	};

	GetActualSessionStartTime = (sessionUUID: string, token: string): Promise<any> => {
		this.abortLast('GetActualSessionStartTime');
		const abortController = this.makeAbortable('GetActualSessionStartTime');
		return Get(`/v3/admin/metrics/sessions/${sessionUUID}/actual-session-start-time`, token, abortController);
	};

	GetChannelMinDate = (channel: number, token: string): Promise<{ timestamp: string }> => {
		// no abort controller for this
		return GetJson<{ timestamp: string }>({ path: `/v3/admin/metrics/channels/${channel}/min-date`, token, metrics: true });
	};
}

export default new AnalyticsFetcher();